import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@ui/Button';
import { FloatingShapes } from '@ui/FloatingShapes';
import { useAuthStore } from '@stores/authStore';
import { authService } from '@services/authService';

type UsernameStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

// 前端保留词（小词表，与后端同步）
const RESERVED_USERNAMES = new Set([
  'admin', 'root', 'system', 'administrator', 'superuser', 'superadmin',
  'sysadmin', 'moderator', 'operator', 'manager', 'manage',
  'api', 'test', 'demo', 'debug', 'localhost', 'server', 'client',
  'webmaster', 'support', 'helpdesk', 'service', 'backend', 'frontend',
  'login', 'register', 'signup', 'about', 'contact', 'faq',
  'terms', 'privacy', 'policy', 'legal', 'copyright',
  'settings', 'config', 'profile', 'account', 'user', 'users',
  'anonymous', 'null', 'undefined', 'default', 'guest', 'aikidcode', 
  'aikid', 'kidcode', 'aicode',
  '管理员', '系统', '官方', '客服', '超级管理员', '版主',
  '运营', '官方客服', '平台', '官方账号',
]);

const SENSITIVE_WORDS = [
  'fuck', 'shit', 'bitch', 'asshole', 'bastard', 'damn',
  '傻逼', '操你', '草泥马', '尼玛', '去死', '废物', '垃圾人',
  'porn', 'sex', 'nude', 'naked', 'hentai',
  '色情', '黄色', '裸体',
  '广告', '代购', '刷单', '兼职', '赚钱', '免费领',
];

const USERNAME_REGEX = /^[\u4e00-\u9fa5a-zA-Z0-9_]{5,20}$/;

function getBorderColor(status: UsernameStatus): string {
  switch (status) {
    case 'checking': return 'border-blue-400';
    case 'available': return 'border-green-500';
    case 'taken':
    case 'invalid': return 'border-red-500';
    default: return 'border-gray-300';
  }
}

function getStatusColor(status: UsernameStatus): string {
  switch (status) {
    case 'checking': return 'text-blue-500';
    case 'available': return 'text-green-600';
    case 'taken':
    case 'invalid': return 'text-red-600';
    default: return 'text-gray-500';
  }
}

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isLoading, error, clearError } = useAuthStore();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    invite_code: '',
    age: '15+',
    first_name: '',
    last_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // 用户名验证状态
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const [usernameMessage, setUsernameMessage] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const successMessage = location.state?.message;

  useEffect(() => {
    clearError();
  }, [clearError]);

  const checkUsernameAvailability = useCallback(async (username: string) => {
    try {
      const result = await authService.checkUsernameAvailable(username);
      if (result.available) {
        setUsernameStatus('available');
        setUsernameMessage('用户名可用');
      } else {
        setUsernameStatus('taken');
        setUsernameMessage(result.message || '该用户名不可用');
      }
    } catch {
      // 网络错误时不阻塞用户，保持当前状态
      setUsernameStatus('idle');
      setUsernameMessage('');
    }
  }, []);

  const handleUsernameChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, username: value }));

    // 清除旧的定时器
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }

    if (!value.trim()) {
      setUsernameStatus('idle');
      setUsernameMessage('');
      return;
    }

    // 前端本地验证
    if (value.length < 3) {
      setUsernameStatus('invalid');
      setUsernameMessage('用户名至少需要5个字符');
      return;
    }

    if (value.length > 20) {
      setUsernameStatus('invalid');
      setUsernameMessage('用户名不能超过20个字符');
      return;
    }

    if (!USERNAME_REGEX.test(value)) {
      setUsernameStatus('invalid');
      setUsernameMessage('用户名只能包含中文、字母、数字和下划线');
      return;
    }

    if (RESERVED_USERNAMES.has(value.toLowerCase())) {
      setUsernameStatus('invalid');
      setUsernameMessage('该用户名为系统保留名称，不可使用');
      return;
    }

    const lower = value.toLowerCase();
    const sensitive = SENSITIVE_WORDS.find(w => lower.includes(w.toLowerCase()));
    if (sensitive) {
      setUsernameStatus('invalid');
      setUsernameMessage('用户名包含不允许的内容，请更换');
      return;
    }

    // 本地验证通过，debounce 后调用后端唯一性检查
    setUsernameStatus('checking');
    setUsernameMessage('正在检查用户名...');

    debounceTimer.current = setTimeout(() => {
      checkUsernameAvailability(value);
    }, 500);
  }, [checkUsernameAvailability]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 提交前再次检查用户名状态
    if (usernameStatus !== 'available') {
      if (!formData.username.trim()) {
        setUsernameStatus('invalid');
        setUsernameMessage('请输入用户名');
      }
      return;
    }

    try {
      await register(formData);
      setRegisteredEmail(formData.email);
      setRegistrationComplete(true);
    } catch (err: any) {
      console.error('Registration error:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'username') {
      handleUsernameChange(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  if (registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700 p-4 relative overflow-hidden">
        <FloatingShapes />
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              注册成功
            </h2>
            <p className="text-gray-600 mb-4">
              验证邮件已发送到 <strong>{registeredEmail}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              请查收邮件并点击验证链接，验证后即可登录。如果未收到邮件，请检查垃圾邮件文件夹。
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
            >
              前往登录
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700 p-4 relative overflow-hidden">
      <FloatingShapes />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-2xl flex items-center justify-center shadow-lg">
            <img src="/logo.png" alt="Logo" className="w-12 h-12" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            注册账户
          </h2>
          <p className="text-blue-100 text-sm">开始你的AI学习之旅</p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl">
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
              <p className="text-sm text-green-600">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                用户名 *
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${getBorderColor(usernameStatus)} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="5-20个字符，中文/字母/数字/下划线"
                disabled={isLoading}
                maxLength={20}
              />
              {usernameMessage && (
                <div className={`flex items-center mt-1 text-xs ${getStatusColor(usernameStatus)}`}>
                  {usernameStatus === 'checking' && (
                    <svg className="animate-spin h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {usernameStatus === 'available' && (
                    <svg className="h-3.5 w-3.5 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {(usernameStatus === 'invalid' || usernameStatus === 'taken') && (
                    <svg className="h-3.5 w-3.5 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span>{usernameMessage}</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                邮箱 *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="请输入邮箱地址"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                密码 *
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="请输入密码（至少8位字符）"
                  minLength={8}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">密码必须包含至少8个字符，建议使用字母、数字和特殊字符组合</p>
            </div>

            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                确认密码 *
              </label>
              <div className="relative">
                <input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="请再次输入密码（至少8位字符）"
                  minLength={8}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="invite_code" className="block text-sm font-medium text-gray-700 mb-1">
                邀请码（可选）
              </label>
              <input
                id="invite_code"
                name="invite_code"
                type="text"
                value={formData.invite_code}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="如有邀请码请输入"
                disabled={isLoading}
              />
            </div>

            {/* <div>
              <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                年龄
              </label>
              <select
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                <option value="6-8">6-8岁</option>
                <option value="9-11">9-11岁</option>
                <option value="12-14">12-14岁</option>
                <option value="15+">15岁以上</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                  名字（可选）
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="请输入名字"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                  姓氏（可选）
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="请输入姓氏"
                  disabled={isLoading}
                />
              </div>
            </div> */}

            <div className="text-sm text-gray-600">
              <p>注册即表示您同意我们的<a href="/terms" className="text-blue-600 hover:underline">服务条款</a>和<a href="/privacy" className="text-blue-600 hover:underline">隐私政策</a>。</p>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={isLoading}
            >
              {isLoading ? '注册中...' : '立即注册'}
            </Button>

            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                已有账户？{' '}
                <a
                  href="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  立即登录
                </a>
              </p>
            </div>
          </form>
        </div>

        <div className="text-center mt-6 text-blue-100 text-sm">
          <p>&copy; 2024 少儿AI编程学习平台. 让每个孩子都能创造AI</p>
        </div>
      </div>
    </div>
  );
};
